import dayjs from 'dayjs';
import {
  ContestBadge,
  ContestParticipatingSortType,
  ContestType,
  PiracySymbol,
} from 'types/marketplace/operation/contest';
import { Nullable } from 'types/common';
import { getUtcDateDash } from 'modules/utils/time';
import { SelectOption } from 'types/common/select';
import { AZURE_STORAGE_PREFIX } from 'constants/common/url';
import { connectSubDomain } from 'services/config';

export const CONTEST_FIRST_YEAR = 2023; // Contest service first launch year
export const TODAY = getUtcDateDash(new Date());

const START_DATE_INTERVAL = 14; // Contest Start Date Interval
const DATE_INTERVAL = 7;
export const INIT_NOTICE_DATE = dayjs(Date()).add(START_DATE_INTERVAL, 'days'); // Contest Notice Default start date.
export const INIT_SUBMISSION_DATE = INIT_NOTICE_DATE.add(DATE_INTERVAL, 'days'); // Contest Submission Default start date.
export const INIT_EVALUATION_DATE = INIT_SUBMISSION_DATE.add(DATE_INTERVAL, 'days'); // Contest Evaluation Default start date.
export const INIT_ANNOUNCEMENT_DATE = INIT_EVALUATION_DATE.add(DATE_INTERVAL, 'days'); // Contest Announcement Default start date.
export const INIT_CLOSED_DATE = INIT_ANNOUNCEMENT_DATE.add(DATE_INTERVAL, 'days'); // Contest Closed Default start date.

export const MESSAGE_REQUIRED_VALIDATED = 'This field is required';

export const CONTEST_BADGE_LIST = [
  { label: 'Platinum', value: ContestBadge.PLATINUM },
  { label: 'Gold', value: ContestBadge.GOLD },
  { label: 'Silver', value: ContestBadge.SILVER },
  { label: 'Bronze', value: ContestBadge.BRONZE },
];

export const REGEX_URL_PATH = /^(http|https):\/\/[^\s/$.?#]+\.\S+$/;

export const CONTEST_LIMIT_SIZE = 20;
export const SCHEDULE_DATE_COUNT = 5; // Stages of the contest

export const LAYOUT_BORDER_COLOR = '#545454'; // AwardLayout's border color
export const LAYOUT_BACKGROUND_COLOR = '#d9d9d9'; // AwardLayout's background color

export const LAYOUT_DEFAULT_WIDTH = 83; // AwardLayout's default width
export const LAYOUT_DEFAULT_HEIGHT = 49; // AwardLayout's default height
export const IMAGE_DEFAULT_HEIGHT = 36; // AwardLayoutImage's default height

export const SORT_OPTIONS: SelectOption[] = [
  { value: ContestParticipatingSortType.AWARD, label: 'Award' },
  { value: ContestParticipatingSortType.UPLOAD_DATE, label: 'Upload Date' },
  { value: ContestParticipatingSortType.CREATOR_ACCOUNT, label: 'Creator Account' },
  { value: ContestParticipatingSortType.LIKES, label: 'Likes' },
];

export const EXCEL_HEADER = [
  { header: 'Item Number.', key: 'itemNumber', width: 15 },
  { header: 'Thumbnail', key: 'imagePath', width: 13 },
  { header: 'Title', key: 'title', width: 30 },
  { header: 'Creator.', key: 'creator', width: 20 },
  { header: 'Item Status', key: 'status', width: 20 },
  { header: 'Likes', key: 'likeCount', width: 15 },
  { header: 'MD Pick', key: 'mdPick', width: 10 },
  { header: 'SW Crack', key: 'piracySymbol', width: 10 },
  { header: 'Upload Date', key: 'registeredDate', width: 30 },
  { header: 'URL', key: 'detailPageUrl', width: 30 },
];

export const EXCEL_EXPORT_LIMIT = 99999;

export enum PREVIEW_TAB_VALUES {
  INFORMATION = 'information',
  SUBMISSIONS = 'submissions',
  MY_WORKS = 'myworks',
  AWARDS = 'awards',
}

export const UserDefaultImageIcon = `${AZURE_STORAGE_PREFIX}/public/backoffice/images/icon/userDefaultImage.svg`;
export const CONNECT_CONTEST_URL = `https://${connectSubDomain}.clo-set.com/contest`;

export const TITLE_LIMIT_LENGTH = 60;
export const HOST_NAME_LIMIT_LENGTH = 50;
export const HOST_SITE_LIMIT_LENGTH = 100;

export const ContestTypeLabel = {
  [ContestType.PUBLIC]: 'Public',
  [ContestType.PRIVATE]: 'Private',
};

export const MD_PICK_YES = '✓';
export const MD_PICK_NO = '';

// [EN] Not exported: use getSwCrackLabel instead — a bare `piracySymbol ? YES : NO`
//      would wrongly show 'Crack' for WARNING (past crack-use history only).
// [KO] 의도적으로 export 하지 않는다. getSwCrackLabel 을 거치지 않고
//      `piracySymbol ? YES : NO` 로 쓰면 WARNING(과거 이력만 있음)도 'Crack' 이 된다.
const SW_CRACK_YES = 'Crack';
const SW_CRACK_NO = '';

// [EN] Spec: show 'Crack' only for CRACKED (current version is cracked). WARNING
//      (past crack-use history), UNKNOWN (no crack info), and null (not inspected)
//      are all left blank — deliberately, per the planning decision.
// [KO] 기획: CRACKED(현재 버전이 크랙)만 'Crack' 으로 표기한다. WARNING(과거 크랙
//      사용 이력)·UNKNOWN(정보 없음)·null(미검사)은 의도적으로 빈칸이다.
export const getSwCrackLabel = (piracySymbol: Nullable<PiracySymbol>): string =>
  piracySymbol === PiracySymbol.CRACKED ? SW_CRACK_YES : SW_CRACK_NO;

export const SW_CRACK_TOOLTIP_TITLE = 'How it works:';
export const SW_CRACK_TOOLTIP_LINES = [
  'Introduced in version 2025.1, this feature detects forced tampering or unauthorized modifications to EXE files on Windows.',
  'File Tagging: Any file saved using a cracked version will have embedded data identifying it as such.',
  'Supported Versions: Detection only applies to files saved in Windows version 2025.1 and later.',
  'OS Limitations: Crack detection is not supported on macOS.',
];
